import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "./adminApi";
import { toast } from "sonner";

// Stable client-side row IDs for blind_spots so React keys don't shift on add/remove
// (avoids focus / cursor jumps when editing the list).
let _rowSeq = 0;
const nextRowId = () => `row-${++_rowSeq}-${Date.now().toString(36)}`;
const toRows = (arr) => (arr || []).map(t => ({ id: nextRowId(), text: t }));
const toStrings = (rows) => (rows || []).map(r => r.text);

export default function AdminArchetypes() {
    const [items, setItems] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [blindRows, setBlindRows] = useState([]);  // [{id, text}]
    const [saving, setSaving] = useState(false);

    const hydrateDraft = (a) => {
        setDraft({ ...a });
        setBlindRows(toRows(a.blind_spots));
    };

    const load = useCallback(async () => {
        const arch = await adminApi.archetypes();
        setItems(arch);
        if (!activeId && arch.length) {
            setActiveId(arch[0].id);
            hydrateDraft(arch[0]);
        }
    }, [activeId]);

    useEffect(() => { load(); }, [load]);

    const selectArch = (a) => {
        setActiveId(a.id);
        hydrateDraft(a);
    };

    const setField = (k, v) => setDraft(prev => ({ ...prev, [k]: v }));
    const setBlindText = (id, v) =>
        setBlindRows(prev => prev.map(r => r.id === id ? { ...r, text: v } : r));
    const addBlind = () =>
        setBlindRows(prev => [...prev, { id: nextRowId(), text: "" }]);
    const removeBlind = (id) =>
        setBlindRows(prev => prev.filter(r => r.id !== id));

    const save = async () => {
        setSaving(true);
        try {
            const patch = {
                name: draft.name,
                tagline: draft.tagline,
                full_description: draft.full_description,
                attracting_pattern: draft.attracting_pattern,
                blind_spots: toStrings(blindRows).filter(Boolean),
                ideal_partner_id: draft.ideal_partner_id,
            };
            const updated = await adminApi.updateArchetype(activeId, patch);
            toast.success("Saved");
            setItems(prev => prev.map(x => x.id === updated.id ? updated : x));
            hydrateDraft(updated);
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-10 max-w-6xl" data-testid="admin-archetypes">
            <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Copy</span>
            <h1 className="mt-2 font-display text-5xl text-burgundy">
                <span className="italic">The</span> archetypes
            </h1>

            <div className="mt-8 grid grid-cols-[260px_1fr] gap-6">
                {/* List */}
                <div className="border border-burgundy/15 bg-cream max-h-[70vh] overflow-y-auto">
                    {items.map(a => (
                        <button
                            key={a.id}
                            data-testid={`arch-row-${a.id}`}
                            onClick={() => selectArch(a)}
                            className={`w-full text-left px-4 py-3 border-b border-burgundy/10 last:border-b-0 transition-colors ${
                                activeId === a.id ? "bg-burgundy/[0.06] text-burgundy" : "hover:bg-burgundy/[0.03]"
                            }`}
                        >
                            <div className="font-display text-base leading-tight">{a.name}</div>
                            <div className="text-[10px] tracking-[0.24em] uppercase text-ink-muted mt-0.5">{a.id}</div>
                        </button>
                    ))}
                </div>

                {/* Editor */}
                {!draft ? (
                    <div className="text-ink-muted text-sm">Select an archetype to edit.</div>
                ) : (
                    <div className="space-y-6">
                        <FieldRow label="Name">
                            <input
                                data-testid="arch-name"
                                value={draft.name || ""}
                                onChange={e => setField("name", e.target.value)}
                                className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-2xl text-burgundy focus:outline-none"
                            />
                        </FieldRow>

                        <FieldRow label="Tagline">
                            <input
                                data-testid="arch-tagline"
                                value={draft.tagline || ""}
                                onChange={e => setField("tagline", e.target.value)}
                                className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display italic text-lg text-ink focus:outline-none"
                            />
                        </FieldRow>

                        <FieldRow label="Full description">
                            <textarea
                                data-testid="arch-description"
                                value={draft.full_description || ""}
                                onChange={e => setField("full_description", e.target.value)}
                                rows={10}
                                className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-ink leading-relaxed focus:outline-none"
                            />
                        </FieldRow>

                        <FieldRow label="Attracting pattern">
                            <textarea
                                data-testid="arch-pattern"
                                value={draft.attracting_pattern || ""}
                                onChange={e => setField("attracting_pattern", e.target.value)}
                                rows={6}
                                className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-ink leading-relaxed focus:outline-none"
                            />
                        </FieldRow>

                        <FieldRow label="Ideal partner ID">
                            <select
                                data-testid="arch-ideal"
                                value={draft.ideal_partner_id || ""}
                                onChange={e => setField("ideal_partner_id", e.target.value)}
                                className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-2.5 font-display text-base text-burgundy focus:outline-none"
                            >
                                {items.map(a => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
                            </select>
                        </FieldRow>

                        <FieldRow label="Blind spots">
                            <div className="space-y-2">
                                {blindRows.map((row, i) => (
                                    <div key={row.id} className="flex gap-2 items-center">
                                        <input
                                            data-testid={`arch-blind-${i}`}
                                            value={row.text}
                                            onChange={e => setBlindText(row.id, e.target.value)}
                                            className="flex-1 bg-cream border border-burgundy/20 focus:border-burgundy p-2 font-display text-base text-ink focus:outline-none"
                                        />
                                        <button
                                            onClick={() => removeBlind(row.id)}
                                            className="text-[10px] tracking-[0.24em] uppercase text-burgundy px-2"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={addBlind}
                                    className="text-[10px] tracking-[0.28em] uppercase text-burgundy underline-offset-4 hover:underline"
                                >
                                    + Add blind spot
                                </button>
                            </div>
                        </FieldRow>

                        <div className="pt-2">
                            <button
                                data-testid="arch-save-btn"
                                onClick={save}
                                disabled={saving}
                                className="bg-burgundy text-cream uppercase text-xs tracking-[0.22em] py-3 px-8 hover:bg-burgundy-deep transition-colors disabled:opacity-60"
                            >
                                {saving ? "Saving…" : "Save changes"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function FieldRow({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">{label}</div>
            {children}
        </div>
    );
}
